import React, { useState, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  useColorModeValue,
  Image,
  VStack,
  HStack,
  Container,
  Card,
  CardBody,
  Heading,
  Divider,
  Progress,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  IconButton,
  AspectRatio,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DefaultAuth from 'layouts/auth/Default';
import baseUrl from 'Base_Url/baseUrl';
import { PinInput, PinInputField } from '@chakra-ui/react';
import {
  FiSmartphone,
  FiLock,
  FiSend,
  FiCheckCircle,
  FiClock,
  FiRefreshCw,
  FiArrowLeft,
  FiEye,
  FiShield,
  FiUser,
  FiKey,
} from 'react-icons/fi';
import {
  MdOutlineSecurity,
  MdOutlinePhoneIphone,
  MdFingerprint,
  MdVerifiedUser,
  MdArrowForward,
  MdQrCodeScanner,
} from 'react-icons/md';
import {
  FaFingerprint,
  FaSatelliteDish,
  FaShieldAlt,
  FaUserLock,
  FaBolt,
} from 'react-icons/fa';
import ThemeToggle from 'components/ThemeToggle';

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-10px) rotate(5deg); }
  50% { transform: translateY(-20px) rotate(0deg); }
  75% { transform: translateY(-10px) rotate(-5deg); }
`;

const glow = keyframes`
  0%, 100% { opacity: 0.8; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.7); }
  70% { box-shadow: 0 0 0 20px rgba(66, 153, 225, 0); }
  100% { box-shadow: 0 0 0 0 rgba(66, 153, 225, 0); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function SignIn() {
  const [step, setStep] = useState(1);
  const [phoneNo, setPhoneNo] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(120);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showOtpInToast, setShowOtpInToast] = useState(true);
  const [receivedOtp, setReceivedOtp] = useState('');

  const navigate = useNavigate();
  const toast = useToast();
  const phoneInputRef = useRef(null);

  const primaryGradient = useColorModeValue(
    'linear-gradient(135deg, #000000 0%, #764ba2 100%)',
    'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  );

  const secondaryGradient = useColorModeValue(
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  );

  const glassBg = useColorModeValue(
    'rgba(255, 255, 255, 0.95)',
    'rgba(15, 23, 42, 0.95)',
  );

  const glassBorder = useColorModeValue(
    'rgba(255, 255, 255, 0.2)',
    'rgba(148, 163, 184, 0.1)',
  );

  const glassShadow = useColorModeValue(
    '0 8px 32px rgba(31, 38, 135, 0.37)',
    '0 8px 32px rgba(0, 0, 0, 0.5)',
  );

  const neonGlow = useColorModeValue(
    '0 0 20px rgba(102, 126, 234, 0.5)',
    '0 0 30px rgba(99, 102, 241, 0.7)',
  );

  const inputBg = useColorModeValue(
    'rgba(255, 255, 255, 0.9)',
    'rgba(30, 41, 59, 0.9)',
  );

  const inputTextColor = useColorModeValue('gray.800', 'white');
  const labelColor = useColorModeValue('gray.700', 'gray.200');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');

  const borderColorLight = useColorModeValue(
    'rgba(102, 126, 234, 0.2)',
    'rgba(99, 102, 241, 0.3)',
  );
  const backgroundGradientLight = useColorModeValue(
    'radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(247, 37, 133, 0.05) 0%, transparent 50%)',
    'radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)',
  );
  const rightPanelBg = useColorModeValue(
    'rgba(255, 255, 255, 0.8)',
    'rgba(15, 23, 42, 0.8)',
  );
  const headingColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.300');
  const purpleTextColor = useColorModeValue('purple.600', 'blue.300');
  const focusBorderColor = useColorModeValue('gray.200', 'gray.600');
  const purpleAccentColor = useColorModeValue('purple.500', 'blue.300');
  const grayTextColor = useColorModeValue('gray.600', 'gray.300');
  const linkHoverColor = useColorModeValue('purple.500', 'blue.300');
  const footerBorderColor = useColorModeValue('gray.200', 'gray.700');
  const footerTextColor = useColorModeValue('gray.500', 'gray.400');
  const securityLevelColor = useColorModeValue('purple.500', 'blue.300');
  const progressBg = useColorModeValue('gray.200', 'gray.700');
  const IconBg = useColorModeValue('white', 'gray.800');

  const testBgColor = useColorModeValue('green.50', 'green.900');
  const testBorderColor = useColorModeValue('green.300', 'green.700');
  const testTextColor = useColorModeValue('green.700', 'green.300');
  const blueBgColor = useColorModeValue('blue.50', 'blue.900');
  const blueBorderColor = useColorModeValue('blue.200', 'blue.700');
  const blueTextColor = useColorModeValue('blue.700', 'blue.200');

  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!phoneNo || phoneNo.length !== 10) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid 10-digit mobile number',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    try {
      setIsSendingOtp(true);
      setLoading(true);

      const response = await axios.post(`${baseUrl}/auth/otp`, { phoneNo });

      if (response.data.status) {
        const otpCode = String(response.data.data);
        setReceivedOtp(otpCode);

        if (showOtpInToast) {
          toast({
            title: '🔐 OTP Sent Successfully',
            description: `Verification code sent to ${phoneNo}`,
            status: 'success',
            duration: 6000,
            isClosable: true,
            position: 'top',
            render: ({ onClose }) => (
              <Alert
                status="success"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                borderRadius="2xl"
                bg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                color="white"
                p={6}
                maxW="400px"
                mx="auto"
                position="relative"
                overflow="hidden"
              >
                {/* Background elements */}
                <Box
                  position="absolute"
                  top="-50px"
                  right="-50px"
                  w="150px"
                  h="150px"
                  borderRadius="full"
                  bg="rgba(255,255,255,0.1)"
                  animation={`${float} 8s ease-in-out infinite`}
                />

                <AlertIcon boxSize="50px" color="white" />
                <AlertTitle mt={4} mb={2} fontSize="xl" fontWeight="bold">
                  Authentication Code Ready
                </AlertTitle>
                <AlertDescription>
                  <VStack spacing={3} mt={2}>
                    <Box
                      p={4}
                      bg="rgba(255,255,255,0.2)"
                      borderRadius="lg"
                      border="2px solid rgba(255,255,255,0.3)"
                      backdropFilter="blur(10px)"
                    >
                      <Text
                        fontSize="3xl"
                        fontWeight="bold"
                        letterSpacing="widest"
                      >
                        {otpCode}
                      </Text>
                    </Box>
                    <Text fontSize="sm" opacity={0.9}>
                      Valid for 2 minutes • Sent to ******{phoneNo.slice(-4)}
                    </Text>
                  </VStack>
                </AlertDescription>
                <CloseButton
                  position="absolute"
                  right={3}
                  top={3}
                  color="white"
                  onClick={onClose}
                />
              </Alert>
            ),
          });
        }

        setTimeout(() => {
          setStep(2);
          setRemainingTime(120);
          if (showOtpInToast) {
            setOtp(otpCode);
          }
        }, 800);
      } else {
        throw new Error('Failed to send OTP');
      }
    } catch (error) {
      toast({
        title: 'Transmission Failed',
        description: error.response?.data?.data || 'Network connection issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSendingOtp(false);
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 4) {
      toast({
        title: 'Invalid Code',
        description: 'Please enter the complete 4-digit authentication code',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setIsVerifying(true);
    setLoading(true);

    try {
      const dataToSend = {
        phoneNo: phoneNo,
        otp: otp,
      };

      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const response = await axios.post(
        `${baseUrl}/auth/login`,
        dataToSend,
        config,
      );

      if (
        response.data.code === 200 &&
        response.data?.data?.user.role === 'talati'
      ) {
        toast({
          title: '🚫 Access Restricted',
          description: 'This user role cannot access the dashboard',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
        return;
      } else if (response.data.code === 200) {
        const token = response.data?.data?.tokens?.access?.token;
        const user = response.data?.data?.user;

        toast({
          title: '🎉 Access Granted',
          description: `Welcome ${
            user.name || 'Officer'
          }! Initializing dashboard...`,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top',
        });

        setTimeout(() => {
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          navigate('/admin/uploaded-files');
        }, 1500);
      } else {
        toast({
          title: 'Authentication Failed',
          description: 'The verification code you entered is incorrect',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error) {
      toast({
        title: 'Verification Error',
        description:
          error.response?.data?.message || 'System authentication failure',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsVerifying(false);
      setLoading(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtp(String(value || ''));
  };

  useEffect(() => {
    let timer;
    if (step === 2 && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prev) => prev - 1);
      }, 1000);
    } else if (remainingTime === 0) {
      toast({
        title: 'Code Expired',
        description: 'Please request a new authentication code',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
    return () => clearInterval(timer);
  }, [step, remainingTime, toast]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResendOtp = () => {
    if (remainingTime > 110) {
      toast({
        title: 'Please Wait',
        description: `Next code available in ${remainingTime - 110} seconds`,
        status: 'info',
        duration: 3000,
      });
      return;
    }
    handleSendOtp({ preventDefault: () => {} });
  };

  return (
    <DefaultAuth>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
          
          body {
            font-family: 'Inter', sans-serif;
          }
          
          .cyber-grid {
            background-image: 
              linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px);
            background-size: 50px 50px;
          }
          
          .particle {
            position: absolute;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%);
            animation: float 20s infinite linear;
          }
          
          .scan-line {
            position: absolute;
            width: 100%;
            height: 2px;
            background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.8), transparent);
            animation: scan 3s linear infinite;
          }
          
          @keyframes scan {
            0% { top: 0%; opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
          }
          
          .hologram-effect {
            background: linear-gradient(
              45deg,
              rgba(99, 102, 241, 0.1),
              rgba(168, 85, 247, 0.1),
              rgba(99, 102, 241, 0.1)
            );
            background-size: 400% 400%;
            animation: gradient 15s ease infinite;
          }
          
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>

      {[...Array(20)].map((_, i) => (
        <Box
          key={i}
          className="particle"
          top={`${Math.random() * 100}%`}
          left={`${Math.random() * 100}%`}
          w={`${Math.random() * 100 + 20}px`}
          h={`${Math.random() * 100 + 20}px`}
          animationDelay={`${Math.random() * 20}s`}
          animationDuration={`${Math.random() * 20 + 10}s`}
        />
      ))}

      <Container
        maxW="container.xl"
        h="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
        className="cyber-grid"
      >
        <Box className="scan-line" />

        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg={backgroundGradientLight}
          zIndex={0}
        />

        <Card
          w={{ base: '95%', md: '90%', lg: '1100px' }}
          maxW="1100px"
          borderRadius="2xl"
          overflow="hidden"
          position="relative"
          bg={glassBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={glassBorder}
          boxShadow={glassShadow}
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: primaryGradient,
            opacity: 0.1,
            zIndex: -1,
          }}
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            borderRadius="2xl"
            boxShadow={`inset 0 0 0 1px ${borderColorLight}`}
            pointerEvents="none"
          />

          <CardBody p={0}>
            <Grid
              templateColumns={{ base: '1fr', lg: '1.2fr 1fr' }}
              h={{ base: 'auto', lg: '650px' }}
            >
              <GridItem
                position="relative"
                overflow="hidden"
                p={10}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                className="hologram-effect"
              >
                <Box position="absolute" top={6} right={6} zIndex={10}>
                  <ThemeToggle size="lg" />
                </Box>

                <Box>
                  <Flex align="center" mb={8}>
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="xl"
                      bg={secondaryGradient}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={4}
                      boxShadow={neonGlow}
                      animation={`${glow} 3s ease-in-out infinite`}
                    >
                      <Icon as={FaShieldAlt} boxSize={8} color="white" />
                    </Box>
                    <Box>
                      <Heading size="lg" color={headingColor}>
                        Yavatmal District Dashboard
                      </Heading>
                      <Text fontSize="sm" color={subTextColor}>
                        Secure Government Portal
                      </Text>
                    </Box>
                  </Flex>

                  <Heading size="xl" mb={3} color={headingColor}>
                    {step === 1
                      ? 'Secure Authentication'
                      : 'Identity Verification'}
                  </Heading>
                  <Text fontSize="lg" color={subTextColor} mb={8}>
                    {step === 1
                      ? 'Enter your credentials to access the district dashboard'
                      : 'Verify your identity with the secure code sent to your device'}
                  </Text>
                </Box>

                <Flex
                  justify="center"
                  align="center"
                  flex={1}
                  position="relative"
                >
                  <Box
                    position="relative"
                    w="300px"
                    h="300px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      position="absolute"
                      w="250px"
                      h="250px"
                      borderRadius="full"
                      border="2px dashed"
                      borderColor={purpleAccentColor}
                      animation={`${rotate} 20s linear infinite`}
                    />

                    <Box
                      position="absolute"
                      w="180px"
                      h="180px"
                      borderRadius="full"
                      border="2px solid"
                      borderColor={purpleAccentColor}
                      animation={`${rotate} 15s linear infinite reverse`}
                    />

                    <Box
                      w="120px"
                      h="120px"
                      borderRadius="full"
                      bg={secondaryGradient}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow={neonGlow}
                      animation={`${pulse} 2s infinite`}
                    >
                      <Icon
                        as={step === 1 ? MdFingerprint : MdVerifiedUser}
                        boxSize={12}
                        color="white"
                      />
                    </Box>

                    {[...Array(6)].map((_, i) => (
                      <Box
                        key={i}
                        position="absolute"
                        w="40px"
                        h="40px"
                        borderRadius="lg"
                        bg={IconBg}
                        boxShadow="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        left={`${
                          Math.cos((i * 60 * Math.PI) / 180) * 140 + 125
                        }px`}
                        top={`${
                          Math.sin((i * 60 * Math.PI) / 180) * 140 + 125
                        }px`}
                        animation={`${float} 3s ease-in-out infinite ${
                          i * 0.5
                        }s`}
                      >
                        <Icon
                          as={
                            [
                              FiUser,
                              FiKey,
                              FiShield,
                              FaFingerprint,
                              FaSatelliteDish,
                              FaBolt,
                            ][i]
                          }
                          color={purpleAccentColor}
                          boxSize={5}
                        />
                      </Box>
                    ))}
                  </Box>
                </Flex>

                <Box>
                  <Flex justify="space-between" mb={2}>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color={subTextColor}
                    >
                      Step {step} of 2
                    </Text>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color={purpleTextColor}
                    >
                      {step === 1 ? 'Authentication' : 'Verification'}
                    </Text>
                  </Flex>
                  <Progress
                    value={step === 1 ? 50 : 100}
                    size="sm"
                    borderRadius="full"
                    bg={useColorModeValue('gray.200', 'gray.700')}
                    sx={{
                      '& > div': {
                        background: secondaryGradient,
                      },
                    }}
                  />
                </Box>
              </GridItem>

              <GridItem
                p={10}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                bg={rightPanelBg}
                backdropFilter="blur(10px)"
              >
                {step === 1 ? (
                  <Box animation={`${slideIn} 0.5s ease-out`}>
                    <VStack spacing={8} align="stretch">
                      <Box>
                        <Flex align="center" mb={4}>
                          <Box
                            w="12px"
                            h="12px"
                            borderRadius="full"
                            bg={secondaryGradient}
                            mr={3}
                            animation={`${pulse} 2s infinite`}
                          />
                          <Heading size="lg" color={headingColor}>
                            Enter Mobile Number
                          </Heading>
                        </Flex>
                        <Text color={subTextColor}>
                          We'll send a secure verification code to your
                          registered device
                        </Text>
                      </Box>

                      <form onSubmit={handleSendOtp}>
                        <VStack spacing={6}>
                          <FormControl>
                            <FormLabel
                              fontSize="sm"
                              fontWeight="semibold"
                              color={labelColor}
                              display="flex"
                              alignItems="center"
                              gap={2}
                            >
                              <Icon as={MdOutlinePhoneIphone} />
                              Mobile Number
                            </FormLabel>
                            <Input
                              ref={phoneInputRef}
                              type="tel"
                              placeholder="Enter 10-digit number"
                              value={phoneNo}
                              onChange={(e) =>
                                setPhoneNo(e.target.value.replace(/\D/g, ''))
                              }
                              maxLength={10}
                              size="lg"
                              h="56px"
                              bg={inputBg}
                              border="2px solid"
                              borderColor={focusBorderColor}
                              color={inputTextColor}
                              _placeholder={{ color: placeholderColor }}
                              _focus={{
                                borderColor: 'purple.500',
                                boxShadow: `0 0 0 3px rgba(168, 85, 247, 0.2)`,
                              }}
                              _hover={{ borderColor: 'purple.400' }}
                              borderRadius="xl"
                              fontSize="lg"
                              fontWeight="medium"
                            />
                          </FormControl>

                          <Button
                            type="submit"
                            size="lg"
                            h="56px"
                            w="full"
                            bg={primaryGradient}
                            color="white"
                            _hover={{
                              transform: 'translateY(-2px)',
                              boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
                            }}
                            _active={{
                              transform: 'translateY(0)',
                            }}
                            isLoading={isSendingOtp}
                            loadingText="Generating Code..."
                            rightIcon={<FiSend />}
                            borderRadius="xl"
                            fontWeight="bold"
                            fontSize="md"
                            boxShadow="0 10px 30px rgba(102, 126, 234, 0.3)"
                          >
                            Generate Secure Code
                          </Button>
                        </VStack>
                      </form>

                      <Divider />

                      <Box
                        p={4}
                        borderRadius="xl"
                        bg={blueBgColor}
                        border="1px solid"
                        borderColor={blueBorderColor}
                      >
                        <Flex align="center" gap={3}>
                          <Icon as={FiEye} color="blue.500" boxSize={5} />
                          <Text fontSize="sm" color={blueTextColor}>
                            <strong>Development Mode:</strong> OTP will be
                            displayed for testing. In production, codes are sent
                            securely via SMS.
                          </Text>
                        </Flex>
                      </Box>
                    </VStack>
                  </Box>
                ) : (
                  <Box animation={`${slideIn} 0.5s ease-out`}>
                    <VStack spacing={8} align="stretch">
                      <Box>
                        <Button
                          variant="ghost"
                          leftIcon={<FiArrowLeft />}
                          onClick={() => {
                            setStep(1);
                            setOtp('');
                          }}
                          size="sm"
                          color={grayTextColor}
                          mb={4}
                          _hover={{ color: linkHoverColor }}
                        >
                          Use different number
                        </Button>

                        <Flex align="center" mb={4}>
                          <Box
                            w="12px"
                            h="12px"
                            borderRadius="full"
                            bg={secondaryGradient}
                            mr={3}
                            animation={`${pulse} 2s infinite`}
                          />
                          <Heading size="lg" color={headingColor}>
                            Enter Verification Code
                          </Heading>
                        </Flex>

                        <Text color={subTextColor} mb={2}>
                          Enter the 4-digit code sent to{' '}
                          <Text
                            as="span"
                            fontWeight="bold"
                            color={purpleTextColor}
                          >
                            ******{phoneNo.slice(-4)}
                          </Text>
                        </Text>

                        {showOtpInToast && receivedOtp && (
                          <Box
                            mt={4}
                            p={3}
                            borderRadius="lg"
                            bg={testBgColor}
                            border="1px solid"
                            borderColor={testBorderColor}
                          >
                            <Flex align="center" gap={2}>
                              <Icon as={MdQrCodeScanner} color="green.500" />
                              <Text fontSize="sm" color={testTextColor}>
                                <strong>Test Code:</strong>{' '}
                                <Text as="span" fontWeight="bold" fontSize="lg">
                                  {receivedOtp}
                                </Text>
                              </Text>
                            </Flex>
                          </Box>
                        )}
                      </Box>

                      <form onSubmit={handleVerifyOtp}>
                        <VStack spacing={6}>
                          <FormControl>
                            <FormLabel
                              fontSize="sm"
                              fontWeight="semibold"
                              color={labelColor}
                              display="flex"
                              alignItems="center"
                              gap={2}
                            >
                              <Icon as={FiLock} />
                              Security Code
                            </FormLabel>
                            <HStack justify="center" spacing={4}>
                              <PinInput
                                otp
                                value={otp || ''}
                                onChange={handleOtpChange}
                                type="alphanumeric"
                                size="lg"
                                autoFocus
                              >
                                {Array(4)
                                  .fill(0)
                                  .map((_, index) => (
                                    <PinInputField
                                      key={index}
                                      h="70px"
                                      w="70px"
                                      fontSize="3xl"
                                      fontWeight="bold"
                                      border="2px solid"
                                      borderColor={focusBorderColor}
                                      bg={inputBg}
                                      color={inputTextColor}
                                      _focus={{
                                        borderColor: 'purple.500',
                                        boxShadow: `0 0 0 3px rgba(168, 85, 247, 0.2)`,
                                      }}
                                      _hover={{ borderColor: 'purple.400' }}
                                      borderRadius="xl"
                                      transition="all 0.2s"
                                    />
                                  ))}
                              </PinInput>
                            </HStack>
                          </FormControl>

                          <Box>
                            <Flex justify="space-between" align="center" mb={3}>
                              <Flex align="center" gap={2}>
                                <Icon as={FiClock} color="orange.500" />
                                <Text fontSize="sm" color={subTextColor}>
                                  Code expires in:{' '}
                                  <Text
                                    as="span"
                                    fontWeight="bold"
                                    color="orange.500"
                                  >
                                    {formatTime(remainingTime)}
                                  </Text>
                                </Text>
                              </Flex>
                              <Button
                                variant="link"
                                colorScheme="purple"
                                size="sm"
                                onClick={handleResendOtp}
                                leftIcon={<FiRefreshCw />}
                                isLoading={isSendingOtp}
                                isDisabled={remainingTime > 110}
                                _hover={{ textDecoration: 'none' }}
                              >
                                Resend Code
                              </Button>
                            </Flex>

                            <Progress
                              value={(remainingTime / 120) * 100}
                              size="sm"
                              borderRadius="full"
                              bg={progressBg}
                              sx={{
                                '& > div': {
                                  background:
                                    remainingTime > 60
                                      ? 'linear-gradient(90deg, #10b981, #059669)'
                                      : remainingTime > 30
                                      ? 'linear-gradient(90deg, #f59e0b, #d97706)'
                                      : 'linear-gradient(90deg, #ef4444, #dc2626)',
                                },
                              }}
                            />
                          </Box>

                          <Button
                            type="submit"
                            size="lg"
                            h="56px"
                            w="full"
                            bg={primaryGradient}
                            color="white"
                            _hover={{
                              transform: 'translateY(-2px)',
                              boxShadow: '0 20px 40px rgba(102, 126, 234, 0.4)',
                            }}
                            _active={{
                              transform: 'translateY(0)',
                            }}
                            isLoading={isVerifying}
                            loadingText="Verifying Identity..."
                            rightIcon={<MdArrowForward />}
                            borderRadius="xl"
                            fontWeight="bold"
                            fontSize="md"
                            boxShadow="0 10px 30px rgba(102, 126, 234, 0.3)"
                            isDisabled={!otp || otp.length !== 4}
                          >
                            Verify & Access Dashboard
                          </Button>
                        </VStack>
                      </form>

                      <Text
                        fontSize="xs"
                        color={footerTextColor}
                        textAlign="center"
                      >
                        By proceeding, you agree to comply with government
                        security protocols and data protection policies.
                      </Text>
                    </VStack>
                  </Box>
                )}

                {/* Footer */}
                <Box
                  mt={10}
                  pt={6}
                  borderTop="1px solid"
                  borderColor={footerBorderColor}
                >
                  <Flex justify="space-between" align="center">
                    <Text fontSize="xs" color={footerTextColor}>
                      © {new Date().getFullYear()} Yavatmal District
                      Administration
                    </Text>
                    <Text
                      fontSize="xs"
                      color={securityLevelColor}
                      fontWeight="medium"
                    >
                      Security Level: MAXIMUM
                    </Text>
                  </Flex>
                </Box>
              </GridItem>
            </Grid>
          </CardBody>
        </Card>
      </Container>
    </DefaultAuth>
  );
}

export default SignIn;


// import React, { useState, useEffect } from 'react';
// import { keyframes } from '@emotion/react';
// import {
//   Box,
//   Button,
//   Flex,
//   FormControl,
//   FormLabel,
//   Input,
//   Text,
//   useToast,
//   useColorModeValue,
//   VStack,
//   HStack,
//   Heading,
//   Divider,
//   Progress,
//   Icon,
//   InputGroup,
//   InputLeftElement,
// } from '@chakra-ui/react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import DefaultAuth from 'layouts/auth/Default';
// import baseUrl from 'Base_Url/baseUrl';
// import { PinInput, PinInputField } from '@chakra-ui/react';
// import {
//   FiPhone,
//   FiClock,
//   FiRefreshCw,
//   FiArrowLeft,
//   FiCopy,
// } from 'react-icons/fi';
// import { FaShieldAlt } from 'react-icons/fa';
// import ThemeToggle from 'components/ThemeToggle';

// Animations
// const fadeIn = keyframes`
//   from { opacity: 0; transform: translateY(20px); }
//   to { opacity: 1; transform: translateY(0); }
// `;

// const slideIn = keyframes`
//   from { transform: translateX(-20px); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// `;

// function SignIn() {
//   const [step, setStep] = useState(1);
//   const [phoneNo, setPhoneNo] = useState('');
//   const [otp, setOtp] = useState('');
//   const [remainingTime, setRemainingTime] = useState(120);
//   const [isSendingOtp, setIsSendingOtp] = useState(false);
//   const [isVerifying, setIsVerifying] = useState(false);
//   const [receivedOtp, setReceivedOtp] = useState('');
  
//   const navigate = useNavigate();
//   const toast = useToast();

//   // Colors based on the image style
//   const primaryColor = useColorModeValue('#3182CE', '#90CDF4');
//   const secondaryColor = useColorModeValue('#2B6CB0', '#63B3ED');
//   const textColor = useColorModeValue('gray.700', 'gray.200');
//   const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
//   const borderColor = useColorModeValue('gray.200', 'gray.600');
//   const hoverBorderColor = useColorModeValue('blue.400', 'blue.300');
//   const focusBorderColor = useColorModeValue('blue.500', 'blue.400');
//   const bgColor = useColorModeValue('#f8fafc', 'gray.900');

//   const handleSendOtp = async (e) => {
//     e.preventDefault();

//     if (!phoneNo || phoneNo.length !== 10) {
//       toast({
//         title: 'Validation Error',
//         description: 'Please enter a valid 10-digit mobile number',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//         position: 'top-right',
//       });
//       return;
//     }

//     try {
//       setIsSendingOtp(true);

//       const response = await axios.post(`${baseUrl}/auth/otp`, { phoneNo });

//       if (response.data.status) {
//         const otpCode = String(response.data.data);
//         setReceivedOtp(otpCode);

//         toast({
//           title: 'Verification Code Sent',
//           description: `Code has been sent to ******${phoneNo.slice(-4)}`,
//           status: 'success',
//           duration: 4000,
//           isClosable: true,
//           position: 'top-right',
//         });

//         setTimeout(() => {
//           setStep(2);
//           setRemainingTime(120);
//           setOtp(otpCode);
//         }, 1000);
//       } else {
//         throw new Error('Failed to send OTP');
//       }
//     } catch (error) {
//       toast({
//         title: 'Transmission Failed',
//         description: error.response?.data?.data || 'Network error occurred',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//         position: 'top-right',
//       });
//     } finally {
//       setIsSendingOtp(false);
//     }
//   };

//   const handleVerifyOtp = async (e) => {
//     e.preventDefault();

//     if (!otp || otp.length !== 4) {
//       toast({
//         title: 'Invalid Code',
//         description: 'Please enter the complete 4-digit authentication code',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//         position: 'top-right',
//       });
//       return;
//     }

//     setIsVerifying(true);

//     try {
//       const dataToSend = {
//         phoneNo: phoneNo,
//         otp: otp,
//       };

//       const response = await axios.post(
//         `${baseUrl}/auth/login`,
//         dataToSend,
//         {
//           headers: {
//             'Content-Type': 'application/json',
//           },
//         }
//       );

//       if (
//         response.data.code === 200 &&
//         response.data?.data?.user.role === 'talati'
//       ) {
//         toast({
//           title: 'Access Denied',
//           description: 'Insufficient permissions for dashboard access',
//           status: 'error',
//           duration: 4000,
//           isClosable: true,
//           position: 'top-right',
//         });
//         return;
//       } else if (response.data.code === 200) {
//         const token = response.data?.data?.tokens?.access?.token;
//         const user = response.data?.data?.user;
        
//         toast({
//           title: 'Welcome Back!',
//           description: `Welcome ${user.name || 'Officer'}`,
//           status: 'success',
//           duration: 2000,
//           isClosable: true,
//           position: 'top-right',
//         });

//         setTimeout(() => {
//           localStorage.setItem('token', token);
//           localStorage.setItem('user', JSON.stringify(user));
//           navigate('/admin/uploaded-files');
//         }, 1500);
//       } else {
//         toast({
//           title: 'Authentication Failed',
//           description: 'The verification code you entered is incorrect',
//           status: 'error',
//           duration: 3000,
//           isClosable: true,
//           position: 'top-right',
//         });
//       }
//     } catch (error) {
//       toast({
//         title: 'System Error',
//         description: error.response?.data?.message || 'Authentication protocol failure',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//         position: 'top-right',
//       });
//     } finally {
//       setIsVerifying(false);
//     }
//   };

//   const handleOtpChange = (value) => {
//     setOtp(String(value || ''));
//   };

//   useEffect(() => {
//     let timer;
//     if (step === 2 && remainingTime > 0) {
//       timer = setInterval(() => {
//         setRemainingTime((prev) => prev - 1);
//       }, 1000);
//     } else if (remainingTime === 0) {
//       toast({
//         title: 'Code Expired',
//         description: 'Please request a new authentication code',
//         status: 'warning',
//         duration: 3000,
//         isClosable: true,
//         position: 'top-right',
//       });
//     }
//     return () => clearInterval(timer);
//   }, [step, remainingTime, toast]);

//   const formatTime = (seconds) => {
//     const mins = Math.floor(seconds / 60);
//     const secs = seconds % 60;
//     return `${mins}:${secs.toString().padStart(2, '0')}`;
//   };

//   const handleResendOtp = () => {
//     if (remainingTime > 110) {
//       toast({
//         title: 'Please Wait',
//         description: `New code available in ${remainingTime - 110} seconds`,
//         status: 'info',
//         duration: 3000,
//       });
//       return;
//     }
//     handleSendOtp({ preventDefault: () => {} });
//   };

//   const copyToClipboard = () => {
//     navigator.clipboard.writeText(receivedOtp);
//     toast({
//       title: 'Code Copied',
//       description: 'Verification code copied to clipboard',
//       status: 'success',
//       duration: 2000,
//     });
//   };

//   return (
//     <Box minH="100vh" bg={bgColor} w="100%" overflowX="hidden">
//       <style>
//         {`
//           @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          
//           * {
//             margin: 0;
//             padding: 0;
//             box-sizing: border-box;
//           }
          
//           body {
//             font-family: 'Inter', sans-serif;
//             margin: 0;
//             padding: 0;
//             width: 100%;
//             overflow-x: hidden;
//             background: ${bgColor};
//           }
          
//           .form-container {
//             animation: ${fadeIn} 0.6s ease-out;
//           }
          
//           .input-field {
//             transition: all 0.2s ease;
//           }
          
//           .input-field:hover {
//             border-color: ${hoverBorderColor};
//           }
          
//           .step-indicator {
//             transition: all 0.3s ease;
//           }
//         `}
//       </style>

//       <Flex minH="100vh" w="100%">
//         {/* Left Panel - Login Form - FULL WIDTH ON MOBILE */}
//         <Box
//           w={{ base: '100%', lg: '50%' }}
//           minH="100vh"
//           display="flex"
//           alignItems="center"
//           justifyContent={{ base: 'flex-start', lg: 'center' }}
//           p={{ base: 4, md: 8, lg: 12 }}
//           bg={bgColor}
//         >
//           {/* Theme Toggle */}
//           <Box position="absolute" top={4} right={4} zIndex={10}>
//             <ThemeToggle />
//           </Box>

//           <Box w="100%" maxW={{ base: '100%', sm: '420px', lg: '480px' }} mx="auto">
//             {/* Logo/Brand */}
//             <Box mb={{ base: 8, md: 10 }} animation={`${fadeIn} 0.6s ease-out`}>
//               <Flex align="center" gap={3} mb={2}>
//                 <Box
//                   w={{ base: '40px', md: '48px' }}
//                   h={{ base: '40px', md: '48px' }}
//                   borderRadius="lg"
//                   bg={`linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`}
//                   display="flex"
//                   alignItems="center"
//                   justifyContent="center"
//                 >
//                   <Icon as={FaShieldAlt} boxSize={{ base: 5, md: 6 }} color="white" />
//                 </Box>
//                 <Box>
//                   <Heading size={{ base: 'md', md: 'lg' }} color={textColor} fontWeight="bold">
//                     Yavatmal Admin
//                   </Heading>
//                   <Text color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }}>
//                     District Dashboard
//                   </Text>
//                 </Box>
//               </Flex>
//             </Box>

//             {/* Form Container */}
//             <Box className="form-container" w="100%">
//               {/* Step Indicator */}
//               <Box mb={{ base: 6, md: 8 }}>
//                 <Flex align="center" gap={2} mb={4}>
//                   <Box
//                     className="step-indicator"
//                     w={{ base: '32px', md: '36px' }}
//                     h={{ base: '32px', md: '36px' }}
//                     borderRadius="full"
//                     bg={step === 1 ? primaryColor : 'gray.100'}
//                     color={step === 1 ? 'white' : mutedTextColor}
//                     display="flex"
//                     alignItems="center"
//                     justifyContent="center"
//                     fontWeight="bold"
//                   >
//                     1
//                   </Box>
//                   <Box w="40px" h="2px" bg={step === 2 ? primaryColor : 'gray.200'} />
//                   <Box
//                     className="step-indicator"
//                     w={{ base: '32px', md: '36px' }}
//                     h={{ base: '32px', md: '36px' }}
//                     borderRadius="full"
//                     bg={step === 2 ? primaryColor : 'gray.100'}
//                     color={step === 2 ? 'white' : mutedTextColor}
//                     display="flex"
//                     alignItems="center"
//                     justifyContent="center"
//                     fontWeight="bold"
//                   >
//                     2
//                   </Box>
//                 </Flex>
                
//                 <Heading size={{ base: 'md', md: 'lg' }} color={textColor} mb={2}>
//                   {step === 1 ? 'Welcome Back' : 'Verify Your Identity'}
//                 </Heading>
//                 <Text color={mutedTextColor} fontSize={{ base: 'sm', md: 'md' }}>
//                   {step === 1 
//                     ? 'Enter your credentials to access the dashboard' 
//                     : 'Enter the code sent to your device'}
//                 </Text>
//               </Box>

//               {/* Form Content */}
//               {step === 1 ? (
//                 <form onSubmit={handleSendOtp}>
//                   <VStack spacing={{ base: 4, md: 6 }}>
//                     <FormControl>
//                       <FormLabel color={textColor} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
//                         Mobile Number
//                       </FormLabel>
//                       <InputGroup>
//                         <InputLeftElement pointerEvents="none" h={{ base: '44px', md: '48px' }}>
//                           <Icon as={FiPhone} color={mutedTextColor} />
//                         </InputLeftElement>
//                         <Input
//                           type="tel"
//                           placeholder="10-digit mobile number"
//                           value={phoneNo}
//                           onChange={(e) => setPhoneNo(e.target.value.replace(/\D/g, ''))}
//                           maxLength={10}
//                           h={{ base: '44px', md: '48px' }}
//                           className="input-field"
//                           bg="white"
//                           border="1px solid"
//                           borderColor={borderColor}
//                           color={textColor}
//                           _placeholder={{ color: mutedTextColor }}
//                           _hover={{ borderColor: hoverBorderColor }}
//                           _focus={{
//                             borderColor: focusBorderColor,
//                             boxShadow: `0 0 0 2px ${focusBorderColor}20`,
//                           }}
//                           borderRadius="lg"
//                         />
//                       </InputGroup>
//                     </FormControl>

//                     <Button
//                       type="submit"
//                       w="full"
//                       h={{ base: '44px', md: '48px' }}
//                       bg={primaryColor}
//                       color="white"
//                       _hover={{
//                         bg: secondaryColor,
//                         transform: 'translateY(-2px)',
//                         boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
//                       }}
//                       _active={{
//                         transform: 'translateY(0)',
//                       }}
//                       isLoading={isSendingOtp}
//                       loadingText="Sending Code..."
//                       borderRadius="lg"
//                       fontWeight="medium"
//                       fontSize={{ base: 'sm', md: 'md' }}
//                     >
//                       Send Verification Code
//                     </Button>

//                     <Text fontSize={{ base: 'xs', md: 'sm' }} color={mutedTextColor} textAlign="center">
//                       By continuing, you agree to our security protocols and privacy policy
//                     </Text>
//                   </VStack>
//                 </form>
//               ) : (
//                 <Box w="100%">
//                   <Button
//                     variant="ghost"
//                     leftIcon={<FiArrowLeft />}
//                     onClick={() => {
//                       setStep(1);
//                       setOtp('');
//                     }}
//                     size="sm"
//                     color={mutedTextColor}
//                     mb={{ base: 4, md: 6 }}
//                     _hover={{ color: primaryColor }}
//                   >
//                     Use different number
//                   </Button>

//                   <form onSubmit={handleVerifyOtp}>
//                     <VStack spacing={{ base: 4, md: 6 }}>
//                       <FormControl>
//                         <FormLabel color={textColor} fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
//                           Verification Code
//                         </FormLabel>
//                         <Text color={mutedTextColor} fontSize={{ base: 'xs', md: 'sm' }} mb={4}>
//                           Code sent to ******{phoneNo.slice(-4)}
//                         </Text>
                        
//                         <HStack justify="center" spacing={3}>
//                           <PinInput
//                             otp
//                             value={otp || ''}
//                             onChange={handleOtpChange}
//                             type="number"
//                             size={{ base: 'md', md: 'lg' }}
//                             autoFocus
//                           >
//                             {Array(4)
//                               .fill(0)
//                               .map((_, index) => (
//                                 <PinInputField
//                                   key={index}
//                                   h={{ base: '50px', md: '60px' }}
//                                   w={{ base: '50px', md: '60px' }}
//                                   fontSize={{ base: 'lg', md: 'xl' }}
//                                   fontWeight="bold"
//                                   border="1px solid"
//                                   borderColor={borderColor}
//                                   bg="white"
//                                   color={textColor}
//                                   _focus={{
//                                     borderColor: focusBorderColor,
//                                     boxShadow: `0 0 0 2px ${focusBorderColor}20`,
//                                   }}
//                                   _hover={{ borderColor: hoverBorderColor }}
//                                   borderRadius="lg"
//                                 />
//                               ))}
//                           </PinInput>
//                         </HStack>
//                       </FormControl>

//                       {/* Timer and Resend */}
//                       <Box w="full">
//                         <Flex justify="space-between" align="center" mb={2}>
//                           <Flex align="center" gap={2}>
//                             <Icon as={FiClock} color={remainingTime < 30 ? 'orange.500' : primaryColor} />
//                             <Text fontSize={{ base: 'xs', md: 'sm' }} color={mutedTextColor}>
//                               Expires in:{' '}
//                               <Text as="span" fontWeight="bold" color={remainingTime < 30 ? 'orange.500' : primaryColor}>
//                                 {formatTime(remainingTime)}
//                               </Text>
//                             </Text>
//                           </Flex>
//                           <Button
//                             variant="link"
//                             color={primaryColor}
//                             size="sm"
//                             onClick={handleResendOtp}
//                             leftIcon={<FiRefreshCw />}
//                             isLoading={isSendingOtp}
//                             isDisabled={remainingTime > 110}
//                             _hover={{ color: secondaryColor }}
//                           >
//                             Resend Code
//                           </Button>
//                         </Flex>
//                         <Progress
//                           value={(remainingTime / 120) * 100}
//                           size="xs"
//                           borderRadius="full"
//                           bg="gray.100"
//                           sx={{
//                             '& > div': {
//                               bg: remainingTime > 60 ? 'green.400' : 
//                                   remainingTime > 30 ? 'orange.400' : 'red.400',
//                             },
//                           }}
//                         />
//                       </Box>

//                       {/* Development Mode Code Display */}
//                       {receivedOtp && (
//                         <Box
//                           p={3}
//                           borderRadius="lg"
//                           bg="blue.50"
//                           border="1px solid"
//                           borderColor="blue.100"
//                           w="full"
//                         >
//                           <Flex justify="space-between" align="center">
//                             <Text fontSize={{ base: 'xs', md: 'sm' }} color="blue.700">
//                               Test Code: <Text as="span" fontWeight="bold">{receivedOtp}</Text>
//                             </Text>
//                             <Button
//                               size="sm"
//                               variant="ghost"
//                               color="blue.600"
//                               onClick={copyToClipboard}
//                               leftIcon={<FiCopy />}
//                             >
//                               Copy
//                             </Button>
//                           </Flex>
//                         </Box>
//                       )}

//                       <Button
//                         type="submit"
//                         w="full"
//                         h={{ base: '44px', md: '48px' }}
//                         bg={primaryColor}
//                         color="white"
//                         _hover={{
//                           bg: secondaryColor,
//                           transform: 'translateY(-2px)',
//                           boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
//                         }}
//                         _active={{
//                           transform: 'translateY(0)',
//                         }}
//                         isLoading={isVerifying}
//                         loadingText="Verifying..."
//                         borderRadius="lg"
//                         fontWeight="medium"
//                         fontSize={{ base: 'sm', md: 'md' }}
//                         isDisabled={!otp || otp.length !== 4}
//                       >
//                         Verify & Continue
//                       </Button>

//                       <Divider />

//                       <Text fontSize={{ base: 'xs', md: 'sm' }} color={mutedTextColor} textAlign="center">
//                         Having trouble? Contact district IT support
//                       </Text>
//                     </VStack>
//                   </form>
//                 </Box>
//               )}

//               {/* Footer */}
//               <Box mt={{ base: 8, md: 12 }} pt={{ base: 4, md: 6 }} borderTop="1px solid" borderColor={borderColor}>
//                 <Text fontSize={{ base: 'xs', sm: 'xs' }} color={mutedTextColor} textAlign="center">
//                   © {new Date().getFullYear()} Yavatmal District Administration
//                 </Text>
//               </Box>
//             </Box>
//           </Box>
//         </Box>

//         {/* Right Panel - Background Image - HIDDEN ON MOBILE */}
//         <Box
//           w={{ base: '0', lg: '50%' }}
//           minH="100vh"
//           display={{ base: 'none', lg: 'block' }}
//           position="relative"
//           overflow="hidden"
//         >
//           {/* Image Container */}
//           <Box
//             position="absolute"
//             top={0}
//             left={0}
//             right={0}
//             bottom={0}
//             bgImage="url('https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')"
//             bgSize="cover"
//             bgPosition="center"
//           >
//             {/* Overlay */}
//             <Box
//               position="absolute"
//               top={0}
//               left={0}
//               right={0}
//               bottom={0}
//               bg="linear-gradient(135deg, rgba(49, 130, 206, 0.9) 0%, rgba(66, 153, 225, 0.8) 100%)"
//             />
//           </Box>

//           {/* Content Overlay */}
//           <Box
//             position="absolute"
//             bottom={0}
//             left={0}
//             right={0}
//             p={12}
//             color="white"
//             animation={`${slideIn} 0.8s ease-out 0.3s both`}
//           >
//             <Heading size="xl" mb={4} fontWeight="bold">
//               Yavatmal District Administration
//             </Heading>
//             <Text fontSize="lg" mb={6} opacity={0.9}>
//               Empowering efficient governance through technology. Secure, reliable, and modern tools for district management.
//             </Text>
            
//             <VStack spacing={4} align="start">
//               <Flex align="center" gap={3}>
//                 <Box w="8px" h="8px" borderRadius="full" bg="white" />
//                 <Text>Secure Government Portal</Text>
//               </Flex>
//               <Flex align="center" gap={3}>
//                 <Box w="8px" h="8px" borderRadius="full" bg="white" />
//                 <Text>Real-time District Analytics</Text>
//               </Flex>
//               <Flex align="center" gap={3}>
//                 <Box w="8px" h="8px" borderRadius="full" bg="white" />
//                 <Text>Document Management System</Text>
//               </Flex>
//               <Flex align="center" gap={3}>
//                 <Box w="8px" h="8px" borderRadius="full" bg="white" />
//                 <Text>Multi-level Security Protocol</Text>
//               </Flex>
//             </VStack>
//           </Box>

//           {/* Logo in corner */}
//           <Box
//             position="absolute"
//             top={8}
//             left={8}
//             bg="rgba(255, 255, 255, 0.1)"
//             backdropFilter="blur(10px)"
//             p={4}
//             borderRadius="lg"
//             border="1px solid rgba(255, 255, 255, 0.2)"
//           >
//             <Text fontSize="sm" fontWeight="bold" color="white">
//               🏛️ Government Portal
//             </Text>
//           </Box>
//         </Box>
//       </Flex>
//     </Box>
//   );
// }

// export default SignIn;